/**
 * Pure helpers for the Conversion ledger (Reports section; formerly the
 * Workspace Conversion view). No React, no I/O — unit-tested.
 *   - period → date range (this month / this quarter / custom), UTC, half-open
 *     [from, to) to match the backend cohort filter.
 *   - the "reached ≥ stage" cumulative funnel derivation from the backend's
 *     furthest-stage buckets.
 *   - the stage LEDGER derivation (additive accounting: arrived = moved on +
 *     lost here + still deciding + working here, per row).
 *   - close-reason display normalization + merge (import-artifact variants).
 *   - percent formatting.
 */

import type { ConversionCloseReasonWire, ConversionFunnelStageWire } from '@/lib/api';

export interface DateRange {
  from: string; // ISO, inclusive
  to: string; // ISO, exclusive
}

export type ConversionPreset = 'this_month' | 'this_quarter' | 'six_months' | 'all_time';

function isoUTC(y: number, mZeroBased: number, d: number): string {
  return new Date(Date.UTC(y, mZeroBased, d)).toISOString();
}

export function monthRange(now: Date): DateRange {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  return { from: isoUTC(y, m, 1), to: isoUTC(y, m + 1, 1) };
}

export function quarterRange(now: Date): DateRange {
  const y = now.getUTCFullYear();
  const startM = Math.floor(now.getUTCMonth() / 3) * 3; // 0,3,6,9
  return { from: isoUTC(y, startM, 1), to: isoUTC(y, startM + 3, 1) };
}

/** The last 6 calendar months INCLUDING the current one: [start of the month
 *  5 months back, start of next month). Aligned with the referral page's
 *  6-month preset (a rolling half-year window). */
export function sixMonthRange(now: Date): DateRange {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  return { from: isoUTC(y, m - 5, 1), to: isoUTC(y, m + 1, 1) };
}

/** Everything through the end of today. The `from` is a fixed early anchor
 *  (2000-01-01 UTC) — comfortably before any Peace OS tenant's first intake,
 *  and the cohort filter simply matches every intake created since. `to` is
 *  the start of tomorrow (UTC) so today's intakes are included. */
export function allTimeRange(now: Date): DateRange {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const d = now.getUTCDate();
  return { from: isoUTC(2000, 0, 1), to: isoUTC(y, m, d + 1) };
}

export function presetRange(preset: ConversionPreset, now: Date): DateRange {
  switch (preset) {
    case 'this_quarter':
      return quarterRange(now);
    case 'six_months':
      return sixMonthRange(now);
    case 'all_time':
      return allTimeRange(now);
    case 'this_month':
    default:
      return monthRange(now);
  }
}

/**
 * Custom range from two YYYY-MM-DD inputs. `to` is made EXCLUSIVE of the day
 * after the picked end date, so the end date itself is included. Returns null on
 * malformed input or from > to (the caller keeps the last good range / disables).
 */
export function customRange(fromDate: string, toDate: string): DateRange | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fromDate) || !/^\d{4}-\d{2}-\d{2}$/.test(toDate)) return null;
  const f = new Date(`${fromDate}T00:00:00Z`);
  const t = new Date(`${toDate}T00:00:00Z`);
  if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime()) || f.getTime() > t.getTime()) return null;
  return { from: f.toISOString(), to: new Date(t.getTime() + 86_400_000).toISOString() };
}

export interface FunnelReachedStage extends ConversionFunnelStageWire {
  /** Intakes that reached AT LEAST this stage (the monotonically-narrowing funnel). */
  reached_count: number;
}

/**
 * Turn the backend's per-stage FURTHEST buckets into cumulative "reached ≥ stage"
 * counts (sort asc). reached[i] = sum of furthest_count for stages at or beyond i,
 * so the first stage's reached_count == cohort_total. Drop-off between adjacent
 * stages = reached[i] − reached[i+1].
 */
export function reachedAtLeast(funnel: ConversionFunnelStageWire[]): FunnelReachedStage[] {
  const sorted = [...funnel].sort((a, b) => a.sort_order - b.sort_order);
  const out: FunnelReachedStage[] = new Array(sorted.length);
  let acc = 0;
  for (let i = sorted.length - 1; i >= 0; i--) {
    acc += sorted[i]!.furthest_count;
    out[i] = { ...sorted[i]!, reached_count: acc };
  }
  return out;
}

/** 0.42 → "42%". Rounds to the nearest whole percent. */
export function formatPct(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

// ---------------------------------------------------------------------
// The stage LEDGER (founder redesign 2026-07-14): one row per stage with
// ADDITIVE accounting — everyone who arrived at a stage is accounted for
// exactly once on that row.
// ---------------------------------------------------------------------

export interface LedgerRow extends ConversionFunnelStageWire {
  /** Intakes that reached AT LEAST this stage (== the reached-≥ funnel count). */
  arrived: number;
  /** Intakes that progressed past this stage (== the next stage's arrived; 0 for the last). */
  moved_on: number;
  /** Closed unconverted whose FURTHEST stage is this one (genuine loss here). */
  lost_here: number;
  /** Open, not yet converted, whose furthest stage is this one (still deciding). */
  deciding: number;
  /** Converted (won) whose furthest stage is this one (working here / settled here). */
  working: number;
}

/**
 * Derive the ledger rows from the backend's per-stage FURTHEST buckets.
 * INVARIANT (pinned in tests): for every row,
 *   arrived == moved_on + lost_here + deciding + working
 * because arrived − moved_on == furthest_count == the honest decomposition
 * (furthest_closed_unconverted + furthest_in_progress + furthest_converted).
 * Column-wise, Σ lost_here == closed_unconverted, Σ deciding == in_progress,
 * Σ working == converted — the reconciliation strip's numbers exactly.
 */
export function ledgerRows(funnel: ConversionFunnelStageWire[]): LedgerRow[] {
  const reached = reachedAtLeast(funnel);
  return reached.map((s, i) => ({
    ...s,
    arrived: s.reached_count,
    moved_on: reached[i + 1]?.reached_count ?? 0,
    lost_here: s.furthest_closed_unconverted,
    deciding: s.furthest_in_progress,
    working: s.furthest_converted,
  }));
}

// ---------------------------------------------------------------------
// Close-reason display normalization + merge. The data holds import-artifact
// variants ("Closed - No Response" vs "No Response"); the DISPLAY merges them.
// The stored values are never rewritten.
// ---------------------------------------------------------------------

const NOT_RECORDED = 'Not recorded';

/**
 * Case-insensitively strip a leading "Closed - " prefix and trim; NULL/blank
 * → "Not recorded". MIRROR: api/_shared/repos/conversionMetrics.ts
 * normalizeCloseReason must stay semantically identical — the drill's
 * `reason:<value>` segment matches rows by the SAME normalization, so the
 * clicked (merged) count always equals the drilled list length.
 */
export function normalizeCloseReason(raw: string | null): string {
  const trimmed = (raw ?? '').trim();
  if (trimmed === '') return NOT_RECORDED;
  const stripped = trimmed.replace(/^closed\s*-\s*/i, '').trim();
  return stripped === '' ? trimmed : stripped;
}

export interface MergedCloseReason {
  /** The display label (the highest-count variant's normalized casing). */
  label: string;
  /** Sum of the merged variants' counts. */
  count: number;
}

/**
 * Merge the server's raw close_reasons for display: normalize each reason,
 * group case-insensitively on the normalized remainder, sum counts. The label
 * takes the casing of the largest merged variant (first on ties — the server
 * sends count desc). Sorted count desc; "Not recorded" sinks last.
 */
export function mergeCloseReasons(reasons: ConversionCloseReasonWire[]): MergedCloseReason[] {
  const groups = new Map<string, { label: string; count: number; labelCount: number }>();
  for (const r of reasons) {
    const norm = normalizeCloseReason(r.reason);
    const key = norm.toLowerCase();
    const g = groups.get(key);
    if (!g) {
      groups.set(key, { label: norm, count: r.count, labelCount: r.count });
    } else {
      g.count += r.count;
      if (r.count > g.labelCount) {
        g.label = norm;
        g.labelCount = r.count;
      }
    }
  }
  return [...groups.values()]
    .map(({ label, count }) => ({ label, count }))
    .sort((a, b) => {
      if (a.label === NOT_RECORDED) return 1;
      if (b.label === NOT_RECORDED) return -1;
      return b.count - a.count || a.label.localeCompare(b.label);
    });
}
